/**
 * Page template + placeholder. Every screen follows the same frame:
 *
 *   <PageBody>
 *     <PageHeader title help actions />
 *     ...filters on top, then content (DataTable / panels)
 *   </PageBody>
 *
 * placeholder() makes a stand-in for a module that a later sprint fills in. Replace the
 * route's `page` in routes.tsx with the real page when it lands.
 */
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EmptyState, PageBody, PageHeader, Panel } from '../../components/ui';

export interface PlaceholderSpec {
  title: string;
  icon: LucideIcon;
  body: string;
  help?: string;
  /** Which sprint fills it in. */
  sprint: string;
}

export function placeholder(spec: PlaceholderSpec) {
  function PlaceholderPage() {
    const Icon = spec.icon;
    return (
      <PageBody>
        <PageHeader title={spec.title} help={spec.help} />
        <Panel pad={false}>
          <EmptyState
            icon={<Icon aria-hidden />}
            title={`${spec.title} is being built`}
            body={
              <>
                <p>{spec.body}</p>
                <p className="mt-2">This screen arrives with sprint {spec.sprint}.</p>
              </>
            }
            action={
              spec.help ? (
                <Link to={`/help/${spec.help}`} className="inline-flex min-h-touch items-center rounded-tag border border-strong/70 px-4 text-sm font-semibold hover:bg-sunken">
                  Read how it will work
                </Link>
              ) : undefined
            }
          />
        </Panel>
      </PageBody>
    );
  }
  PlaceholderPage.displayName = `Placeholder(${spec.title})`;
  return PlaceholderPage;
}
